// ─────────────────────────────────────────────────────────────────────────────
// MetricsTreeNav — recursively renders metricsNav.js.
//
//   • A node with `children`  → collapsible group (folder).
//   • A node with `reportKey` → selectable leaf (report).
//   • Groups can hold subgroups AND loose leaves at the same level (mixed tree),
//     so the Phase-2 Risk suite drops in with no changes here.
//
// Selection is owned by the parent (MetricsApp); expand/collapse state is local.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState } from "react";
import { isGroup, isLeaf, firstGroupId } from "./metricsNav";

function Leaf({ node, depth, selectedLeafId, onSelect }) {
  const active = node.id === selectedLeafId;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(node)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(node); } }}
      style={{
        display: "flex", alignItems: "center", gap: 8,
        padding: "8px 10px", paddingLeft: 12 + depth * 14,
        borderRadius: 7, cursor: "pointer", marginBottom: 1,
        fontSize: 13, fontWeight: active ? 700 : 500,
        color: active ? "var(--ed-brand)" : "var(--ed-ink-3)",
        background: active ? "var(--ed-brand-soft)" : "transparent",
        borderLeft: active ? "3px solid var(--ed-brand)" : "3px solid transparent",
      }}
    >
      <span style={{ fontSize: 11, opacity: 0.6 }}>📄</span>
      <span style={{ flex: 1 }}>{node.label}</span>
    </div>
  );
}

function Group({ node, depth, selectedLeafId, onSelect, expandedIds, toggle }) {
  const open = expandedIds.has(node.id);
  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        onClick={() => toggle(node.id)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(node.id); } }}
        style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "9px 10px", paddingLeft: 10 + depth * 14,
          borderRadius: 7, cursor: "pointer", marginBottom: 1,
          fontSize: 12.5, fontWeight: 700, color: "var(--ed-ink-2)",
          textTransform: depth === 0 ? "uppercase" : "none",
          letterSpacing: depth === 0 ? ".04em" : "0",
        }}
      >
        <span style={{ fontSize: 10, width: 12, color: "var(--ed-muted-2)", transform: open ? "rotate(90deg)" : "none", transition: "transform .15s" }}>▶</span>
        <span style={{ flex: 1 }}>{node.label}</span>
      </div>
      {open && (
        <div>
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedLeafId={selectedLeafId}
              onSelect={onSelect}
              expandedIds={expandedIds}
              toggle={toggle}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TreeNode(props) {
  const { node } = props;
  if (isGroup(node)) return <Group {...props} />;
  if (isLeaf(node)) return <Leaf {...props} />;
  return null; // malformed node — render nothing rather than crash
}

export default function MetricsTreeNav({ nav, selectedLeafId, onSelect }) {
  // Default state: first top-level group expanded.
  const [expandedIds, setExpandedIds] = useState(() => {
    const set = new Set();
    const g = firstGroupId(nav);
    if (g) set.add(g);
    return set;
  });

  const toggle = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div>
      {(nav || []).map((node) => (
        <TreeNode
          key={node.id}
          node={node}
          depth={0}
          selectedLeafId={selectedLeafId}
          onSelect={onSelect}
          expandedIds={expandedIds}
          toggle={toggle}
        />
      ))}
    </div>
  );
}
